'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState } from 'react'

// walrus
import Box from '../kit/box'

// lib
import { useAuthStore, selectIsAuthenticated } from '@store/Auth'

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

// the store only refreshes a token close to its expiry, so a check is cheap
const REFRESH_CHECK_INTERVAL = 30 * 1000

/* -----------------------------------------------------------------------------
 * Session
 * -------------------------------------------------------------------------- */

export interface SessionProps {
  children: React.ReactNode
}

function Session({ children }: SessionProps) {
  const [initialized, setInitialized] = useState(false)
  const isAuthenticated = useAuthStore(selectIsAuthenticated)

  // An access token lasts 15 minutes, so a stored one has usually expired by
  // the next visit: renew it before anything renders. A refresh token flipper
  // no longer accepts ends the session here.
  useEffect(() => {
    useAuthStore
      .getState()
      .getFreshAccessToken()
      .catch(() => null)
      .then(() => setInitialized(true))
  }, [])

  // Requests renew the token on their own, but the chat socket sends it only
  // when it connects, so keep it fresh while the app is open.
  useEffect(() => {
    if (!initialized || !isAuthenticated) {
      return
    }

    const interval = setInterval(() => {
      useAuthStore
        .getState()
        .getFreshAccessToken()
        .catch(() => null)
    }, REFRESH_CHECK_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [initialized, isAuthenticated])

  // Block rendering until we have determined session state
  return initialized ? <Box flex='1 1 auto'>{children}</Box> : null
}

export default Session
